import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { trackMetaEvent } from "@/lib/metaPixel";
import type { AnyValue } from "@/lib/rowTypes";

interface Household {
  id: string;
  name: string;
  owner_id: string;
  referral_code?: string | null;
  referred_by_code?: string | null;
  founding_member?: boolean | null;
}

interface HouseholdMember {
  id: string;
  user_id: string;
  role: "parent" | "child";
  display_name: string | null;
}

interface BillingInvoice {
  id: string;
  amount_paid: number;
  currency: string;
  status: string;
  created: number;
  hosted_invoice_url: string | null;
  invoice_pdf: string | null;
}

interface PaymentMethodSummary {
  brand: string | null;
  last4: string | null;
  exp_month: number | null;
  exp_year: number | null;
}

interface SubscriptionStatus {
  status: string;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  plan_name: string | null;
  billing_exempt: boolean;
  lifetime_access: boolean;
  payment_method: PaymentMethodSummary | null;
  invoices: BillingInvoice[];
}

interface HouseholdContextPayload {
  household?: Household | null;
  members?: HouseholdMember[] | null;
  subscription?: AnyValue;
  is_parent?: boolean;
}

const EMPTY_SUBSCRIPTION: SubscriptionStatus = {
  status: "inactive",
  current_period_end: null,
  cancel_at_period_end: false,
  plan_name: null,
  billing_exempt: false,
  lifetime_access: false,
  payment_method: null,
  invoices: [],
};

async function loadHouseholdContext(): Promise<HouseholdContextPayload | null> {
  const { data, error } = await supabase.rpc("get_my_household_context");
  if (error) throw error;
  return (data as HouseholdContextPayload) ?? null;
}

function normalizeSubscription(data: AnyValue): SubscriptionStatus {
  return {
    status: data?.status || "inactive",
    current_period_end: data?.current_period_end || null,
    cancel_at_period_end: Boolean(data?.cancel_at_period_end),
    plan_name: data?.plan_name || null,
    billing_exempt: Boolean(data?.billing_exempt),
    lifetime_access: Boolean(data?.lifetime_access),
    payment_method: data?.payment_method || null,
    invoices: Array.isArray(data?.invoices) ? data.invoices : [],
  };
}

/**
 * Permanent free-access override.
 * billing_exempt = true on subscription_records means the user bypasses
 * all payment/subscription checks. In addition, accounts listed in the
 * `qa_test_allowlist` table (enabled + non-expired) are treated as
 * billing-exempt at the client without needing a subscription_records row.
 * Allowlist membership is validated server-side via the
 * `is_qa_test_allowlisted_self` SECURITY DEFINER RPC.
 */
function isBillingExempt(sub: AnyValue): boolean {
  return Boolean(sub?.billing_exempt || sub?.lifetime_access);
}

export function useHousehold() {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const [household, setHousehold] = useState<Household | null>(null);
  const [members, setMembers] = useState<HouseholdMember[]>([]);
  const [subscription, setSubscription] = useState<SubscriptionStatus>(EMPTY_SUBSCRIPTION);
  const [billingExempt, setBillingExempt] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<Error | null>(null);
  const [isParent, setIsParent] = useState(false);

  const refreshSubscriptionByHousehold = useCallback(async (householdId: string) => {
    if (!userId) return EMPTY_SUBSCRIPTION;

    // Add a 8-second timeout so a slow/broken edge function never blocks login
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);

    try {
      const { data, error } = await supabase.functions.invoke("stripe-billing", {
        body: { action: "check_status", household_id: householdId },
      });

      clearTimeout(timeout);
      if (error) throw error;

      const normalized = normalizeSubscription(data);
      setSubscription(normalized);
      return normalized;
    } catch (err) {
      clearTimeout(timeout);
      console.warn("[useHousehold] stripe-billing refresh failed:", err);
      throw err;
    }
  }, [userId]);

  const load = useCallback(async () => {
    let qaAllowlisted = false;

    if (!userId) {
      setHousehold(null);
      setMembers([]);
      setSubscription(EMPTY_SUBSCRIPTION);
      setBillingExempt(false);
      setIsParent(false);
      setLoadError(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setLoadError(null);
    console.log("[useHousehold] Loading household for user:", userId);

    try {
      // QA test allowlist: validated server-side. Allowlisted accounts
      // bypass paywall/beta gates without needing a subscription_records
      // row. Email-based + lower-cased, enabled + non-expired only.
      try {
        const { data: qa } = await (supabase).rpc("is_qa_test_allowlisted_self");
        qaAllowlisted = Boolean(qa);
        if (qaAllowlisted) setBillingExempt(true);
      } catch (qaErr) {
        console.warn("[useHousehold] qa allowlist check failed (non-fatal):", qaErr);
      }

      // Prefer the SECURITY DEFINER context RPC. It avoids recursive RLS policy
      // failures on households/household_members that can occur in preview environments when
      // direct table reads are evaluated before helper policies settle.
      try {
        const ctx = await loadHouseholdContext();
        if (ctx?.household) {
          const contextHousehold = ctx.household as Household;
          const contextMembers = Array.isArray(ctx.members) ? ctx.members : [];
          const contextSub = ctx.subscription;

          setHousehold(contextHousehold);
          setMembers(contextMembers as HouseholdMember[]);
          setIsParent(Boolean(ctx.is_parent));
          setSubscription(contextSub ? normalizeSubscription(contextSub) : EMPTY_SUBSCRIPTION);
          setBillingExempt(isBillingExempt(contextSub) || qaAllowlisted);

          if (!qaAllowlisted && !isBillingExempt(contextSub) && (contextSub?.stripe_customer_id || contextSub?.stripe_subscription_id)) {
            try {
              await refreshSubscriptionByHousehold(contextHousehold.id);
            } catch (refreshError) {
              console.warn("[useHousehold] Stripe refresh failed, using cached subscription:", refreshError);
            }
          }
          return;
        }
      } catch (ctxErr) {
        console.warn("[useHousehold] context RPC failed, falling back to direct table reads:", ctxErr);
      }

      const { data: owned, error: ownedError } = await supabase
        .from("households")
        .select("*")
        .eq("owner_id", userId)
        .limit(1)
        .maybeSingle();

      if (ownedError) throw ownedError;

      if (owned) {
        const ownedHousehold = owned;
        setHousehold(ownedHousehold);
        setIsParent(true);

        const [{ data: mems, error: memError }, { data: sub, error: subError }] = await Promise.all([
          supabase.from("household_members").select("*").eq("household_id", ownedHousehold.id),
          supabase.from("subscription_records").select("*").eq("household_id", ownedHousehold.id).maybeSingle(),
        ]);

        if (memError) throw memError;
        if (subError) throw subError;

        setMembers((mems || []));
        setSubscription(sub ? normalizeSubscription(sub) : EMPTY_SUBSCRIPTION);
        setBillingExempt(isBillingExempt(sub) || qaAllowlisted);

        // Skip Stripe refresh for billing-exempt users — they don't need it
        // and calling the edge function can hang/timeout and block login
        if (!qaAllowlisted && !isBillingExempt(sub) && ((sub)?.stripe_customer_id || (sub)?.stripe_subscription_id)) {
          try {
            await refreshSubscriptionByHousehold(ownedHousehold.id);
          } catch (refreshError) {
            console.warn("[useHousehold] Stripe refresh failed, using cached subscription:", refreshError);
          }
        }
      } else {
        // User is not a household owner — check if they're a member
        const { data: membership, error: membershipError } = await supabase
          .from("household_members")
          .select("*, households(*)")
          .eq("user_id", userId)
          .limit(1)
          .maybeSingle();

        if (membershipError) throw membershipError;

        if (membership) {
          const hh = (membership).households;
          setHousehold(hh);
          setMembers([(membership) as HouseholdMember]);
          setIsParent(false);

          // Child accounts use SECURITY DEFINER RPC to read subscription status
          // (direct table access is restricted to household owners only)
          if (hh?.id) {
            const { data: ctx } = await supabase.rpc("get_my_household_context");
            const sub = (ctx as AnyValue)?.subscription;
            if (sub) {
              setSubscription(normalizeSubscription(sub));
              setBillingExempt(isBillingExempt(sub) || qaAllowlisted);
            } else {
              setBillingExempt(qaAllowlisted);
            }
          }
        } else {
          setHousehold(null);
          setMembers([]);
          setIsParent(false);
          setSubscription(EMPTY_SUBSCRIPTION);
          setBillingExempt(qaAllowlisted);
        }
      }
    } catch (err) {
      console.error("[useHousehold] Failed to load household:", err);
      // CRITICAL: surface the error so SubscriptionGate shows a recoverable
      // error UI instead of silently flashing the paywall.
      setLoadError(err instanceof Error ? err : new Error(String(err)));
      setHousehold(null);
      setMembers([]);
      setSubscription(EMPTY_SUBSCRIPTION);
      setBillingExempt(qaAllowlisted);
      setIsParent(false);
    } finally {
      console.log("[useHousehold] Load complete");
      setLoading(false);
    }
  }, [userId, refreshSubscriptionByHousehold]);

  // Reload only when the actual user IDENTITY changes (not on every token
  // refresh, which produces a new user object reference but same id).
  useEffect(() => {
    setLoading(true);
    void load();
  }, [userId, load]);

  const createHousehold = useCallback(async (name: string) => {
    if (!user) throw new Error("You must be signed in.");

    const trimmedName = name.trim();
    if (!trimmedName) throw new Error("Please enter a household name.");

    // Use the server-side RPC which handles race conditions, member creation,
    // and subscription_records atomically in a single transaction
    const { data, error } = await supabase.rpc("create_household", {
      p_name: trimmedName,
    });

    if (error) throw new Error(error.message);

    const result = data;
    const newHousehold = { id: (result as AnyValue).id, name: (result as AnyValue).name, owner_id: (result as AnyValue).owner_id };

    // Attach referral source if the user arrived via ?ref=CODE.
    // The server-side trigger blocks self-referrals, so this is safe.
    try {
      const refRaw = typeof window !== "undefined" ? localStorage.getItem("heirwise_ref_code") : null;
      const refCode = (refRaw || "").toLowerCase().replace(/[^a-z0-9]/g, "");
      if (refCode) {
        await supabase
          .from("households")
          .update({ referred_by_code: refCode })
          .eq("id", newHousehold.id);
        localStorage.removeItem("heirwise_ref_code");
        // Analytics: a brand-new household attributed to a referral code.
        void trackMetaEvent("referral_signup", {
          email: user.email ?? undefined,
          externalId: user.id,
          customData: { ref_code: refCode, household_id: newHousehold.id },
        });
      }
    } catch (refErr) {
      console.warn("[useHousehold] referral attach failed (non-fatal):", refErr);
    }

    await load();
    return newHousehold;
  }, [user, load]);

  const addChild = useCallback(async (displayName: string, childUserId: string) => {
    if (!user || !household) throw new Error("No household found.");
    if (household.owner_id !== user.id) throw new Error("Only the household owner can add children.");

    const { error } = await supabase.from("household_members").insert({
      household_id: household.id,
      user_id: childUserId,
      role: "child",
      display_name: displayName,
    });

    if (error) throw new Error(error.message);
    await load();
  }, [user, household, load]);

  const removeMember = useCallback(async (memberId: string) => {
    if (!user || !household) throw new Error("No household found.");

    const member = members.find((item) => item.id === memberId);
    if (!member) throw new Error("Member not found.");
    if (member.role === "parent") throw new Error("The household owner cannot be removed.");

    const { error } = await supabase.from("household_members").delete().eq("id", memberId);
    if (error) throw error;

    await load();
  }, [user, household, members, load]);

  const startCheckout = useCallback(async () => {
    if (!user || !household) throw new Error("Create a household before subscribing.");

    const { data, error } = await supabase.functions.invoke("stripe-billing", {
      body: { action: "create_checkout", household_id: household.id },
    });

    if (error) throw error;
    if (data?.url) window.location.href = data.url;
    return data;
  }, [user, household]);

  const openBillingPortal = useCallback(async () => {
    if (!user || !household) throw new Error("No household found.");

    const { data, error } = await supabase.functions.invoke("stripe-billing", {
      body: { action: "manage_billing", household_id: household.id },
    });

    if (error) throw error;
    if (data?.url) {
      const opened = window.open(data.url, "_blank", "noopener,noreferrer");
      if (!opened) window.location.href = data.url;
    }
    return data;
  }, [user, household]);

  const cancelSubscription = useCallback(async () => {
    if (!user || !household) throw new Error("No household found.");

    const { data, error } = await supabase.functions.invoke("stripe-billing", {
      body: { action: "cancel", household_id: household.id },
    });

    if (error) throw error;
    await load();
    return data;
  }, [user, household, load]);

  const reactivateSubscription = useCallback(async () => {
    if (!user || !household) throw new Error("No household found.");

    const { data, error } = await supabase.functions.invoke("stripe-billing", {
      body: { action: "reactivate", household_id: household.id },
    });

    if (error) throw error;

    const normalized = normalizeSubscription(data);
    setSubscription(normalized);
    return normalized;
  }, [user, household]);

  /** Seat-safe plan change; the server rejects downgrades with SEAT_RESOLUTION_REQUIRED. */
  const changePlan = useCallback(async (planKey: string) => {
    if (!user || !household) throw new Error("No household found.");
    const { data, error } = await supabase.functions.invoke("stripe-billing", {
      body: { action: "change_plan", household_id: household.id, plan_key: planKey },
    });
    if (error) throw error;
    if (data?.error) throw new Error(data.error);
    await load();
    return data;
  }, [user, household, load]);

  const refreshSubscription = useCallback(async () => {
    if (!household) return EMPTY_SUBSCRIPTION;
    return refreshSubscriptionByHousehold(household.id);
  }, [household, refreshSubscriptionByHousehold]);

  // billing_exempt or lifetime_access permanently bypasses subscription checks
  const hasActiveSubscription = subscription.status === "active" || subscription.status === "trialing";
  const isSubscribed = billingExempt || hasActiveSubscription;

  return {
    household,
    members,
    subscription,
    isParent,
    isSubscribed,
    hasActiveSubscription,
    billingExempt,
    loading,
    loadError,
    createHousehold,
    addChild,
    removeMember,
    startCheckout,
    openBillingPortal,
    cancelSubscription,
    reactivateSubscription,
    changePlan,
    refreshSubscription,
    refetch: load,
  };
}
